import React, { type MutableRefObject, useCallback } from 'react';
import { Form } from 'antd';
import type { Rule } from 'antd/es/form';
import { useIntl } from 'react-intl';

import { ChargeSummaryAmountField } from '../charge-summary-amount-field';
import { roundCurrency2 } from '../../utils/payment-math';
import {
  syncDownSplitAmountsFromEditedField,
  syncPeerDownSplitAmountOnly,
} from '../../utils/split-amount-sync';

function getPeerSplitDownFieldName(
  fieldName: 'splitDownAmount1' | 'splitDownAmount2',
): 'splitDownAmount1' | 'splitDownAmount2' {
  return fieldName === 'splitDownAmount1' ? 'splitDownAmount2' : 'splitDownAmount1';
}

export interface SplitInstalmentDownChargeFieldProps {
  downTotal: number;
  fieldName: 'splitDownAmount1' | 'splitDownAmount2';
  editedKey: '1' | '2';
  rules: Rule[];
  formItemClassName: string;
  splitDownLastRef: MutableRefObject<'1' | '2'>;
}

export function SplitInstalmentDownChargeField({
  downTotal,
  fieldName,
  editedKey,
  rules,
  formItemClassName,
  splitDownLastRef,
}: SplitInstalmentDownChargeFieldProps): React.ReactElement {
  const intl = useIntl();
  const form = Form.useFormInstance();

  const handleAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      syncPeerDownSplitAmountOnly({
        downTotal,
        editedField: editedKey,
        rawValue: e.target.value,
        setFieldsValue: (patch) => form.setFieldsValue(patch),
        lastEditedRef: splitDownLastRef,
      });
    },
    [downTotal, editedKey, form, splitDownLastRef],
  );

  const handleAmountBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      syncDownSplitAmountsFromEditedField({
        downTotal,
        editedField: editedKey,
        rawValue: e.target.value,
        setFieldsValue: (patch) => form.setFieldsValue(patch),
        lastEditedRef: splitDownLastRef,
      });
      const peerFieldName = getPeerSplitDownFieldName(fieldName);
      void form.validateFields([fieldName, peerFieldName]).catch(() => undefined);
    },
    [downTotal, editedKey, fieldName, form, splitDownLastRef],
  );

  return (
    <ChargeSummaryAmountField
      name={fieldName}
      rules={rules}
      dependencies={[
        'splitDownAmount1',
        'splitDownAmount2',
        'paymentPlan',
        'splitInstalmentDown',
        'downPaymentAmount',
      ]}
      formItemClassName={formItemClassName}
      ariaLabel={intl.formatMessage({ id: 'certificates.payment.downPaymentCardAria' })}
      stepMax={roundCurrency2(downTotal)}
      onChange={handleAmountChange}
      onBlur={handleAmountBlur}
    />
  );
}
